<template>
<v-col class="my-0 py-0 px-0 px-md-8 pb-15">
    <v-col class="text-start mt-5 mb-3">
        <v-alert text dense color="#8061c2" border="left" class="pl-5">
            <h1 class="title mt-1">Agentes de IA</h1>
            <p class="desc mt-0 mb-1">Listado de agentes de IA creados en la plataforma.</p>
        </v-alert>
    </v-col>

    <!-- Barra de búsqueda y filtros -->
    <v-row class="mx-3">
        <v-col cols="12" md="4" class="px-0">
            <v-text-field v-model="search" color="#8061c2" label="Buscar agentes" append-icon="mdi-magnify" single-line hide-details outlined class="mb-2 mx-0 px-0" @input="onSearch"></v-text-field>
        </v-col>

        <!-- Select para tipo de agente -->
        <v-col cols="12" md="2" class="px-md-2 pr-md-0 px-0">
            <v-select v-model="agentType" :items="typeOptions" label="Tipo" outlined @change="onOrderChange"></v-select>
        </v-col>

        <!-- Select para ordenar por campo -->
        <v-col cols="12" md="3" class="px-md-2 px-0">
            <v-select v-model="orderBy" :items="orderByOptions" label="Ordenar por" outlined @change="onOrderChange"></v-select>
        </v-col>

        <!-- Select para dirección de ordenamiento -->
        <v-col cols="12" md="3" class="px-0">
            <v-select v-model="orderDirection" :items="orderDirectionOptions" label="Dirección" outlined @change="onOrderChange"></v-select>
        </v-col>
    </v-row>

    <!-- Tabla de datos -->
    <v-data-table :headers="headers" :items="agents" :loading="loading" class="elevation-1 mx-3 px-2 py-4" :items-per-page="perPage" :server-items-length="totalFiltered" :page.sync="page" :footer-props="{
        itemsPerPageOptions: [50, 100, 200, 300, -1],
        showFirstLastPage: true
      }" @update:page="onPageChange" @update:items-per-page="changePerPage">
      <!-- Columnas -->
      <template v-slot:item.image="{ item }">
        <v-avatar size="40">
          <v-img :src="item.image" v-if="item.image"></v-img>
          <v-avatar v-else color="black">
            <v-icon color="white">mdi-robot</v-icon>
          </v-avatar>
        </v-avatar>
      </template>

      <template v-slot:item.prompt="{ item }">
        <v-icon color="#8061c2" @click="openPromptModal(item)">
          mdi-text-box-edit
        </v-icon>
      </template>

      <template v-slot:item.type.name="{ item }">
        <v-chip :color="getTypeColor(item.type_id)" dark small>
          {{ item.type ? item.type.name : 'N/A' }}
        </v-chip>
      </template>

      <template v-slot:item.status_id="{ item }">
        <v-chip :color="getStatusColor(item.status?.id)" dark small>
          {{ item.status?.name || 'N/A' }}
        </v-chip>
      </template>

      <template v-slot:item.created_at="{ item }">
        {{ item.formatted_created_at }}
      </template>
    </v-data-table>

    <!-- Modal de Prompt -->
    <v-dialog v-model="showPromptModal" max-width="800px">
      <v-card>
        <v-card-title class="headline white--text">
          Prompt del agente
        </v-card-title>

        <v-card-text class="pt-4">
          <p class="subtitle-1 grey--text text-left">Comportamiento del agente al interactuar con el usuario.</p>

          <v-textarea
            v-model="currentPrompt"
            outlined
            rows="10"
            label="Prompt"
            class="mt-3"
            :error-messages="promptError"
          ></v-textarea>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="closePromptModal">
            Cancelar
          </v-btn>
          <v-btn color="success" @click="savePrompt" :loading="savingPrompt">
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-col>
</template>

<script>
import axios from '@/axios.js';

export default {
  name: 'Agents',
  data() {
    return {
      agents: [],
      loading: true,
      page: 1,
      perPage: 50,
      totalFiltered: 0,
      search: '',
      agentType: 0,
      orderBy: 'created_at',
      orderDirection: 'desc',
      showPromptModal: false,
      currentPrompt: '',
      currentAgentId: null,
      promptError: '',
      savingPrompt: false,

      orderByOptions: [
        { text: 'Fecha de creación', value: 'created_at' },
        { text: 'Nombre', value: 'name' }
      ],

      orderDirectionOptions: [
        { text: 'Mayor a menor', value: 'desc' },
        { text: 'Menor a mayor', value: 'asc' }
      ],

      typeOptions: [
        { text: 'Todos', value: 0 },
        { text: 'Q&A', value: 1 }
      ],

      headers: [
        {
          text: 'Avatar',
          value: 'image',
          align: 'start',
          sortable: false
        },
        {
          text: 'Nombre',
          value: 'name'
        },
        {
          text: 'Prompt',
          value: 'prompt',
          align: 'center',
          sortable: false
        },
        {
          text: 'Tipo',
          value: 'type.name',
          align: 'center'
        },
        {
          text: 'Estado',
          value: 'status_id',
          align: 'center'
        },
        {
          text: 'Fecha de creación',
          value: 'created_at'
        },
        /*{
            text: 'Opciones',
            value: 'actions',
            sortable: false
        }*/
      ]
    };
  },

  methods: {
    async fetchAgents() {
      this.loading = true;
      try {
        const response = await axios.get('/admin/get-agents', {
          params: {
            page: this.page,
            per_page: this.perPage,
            search: this.search,
            order_by: this.orderBy,
            order_direction: this.orderDirection,
            filter: this.agentType
          }
        });

        this.agents = response.data.agents.data;
        this.totalFiltered = response.data.agents.total;
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    getTypeColor(typeId) {
      switch (typeId) {
        case 1:
          return '#1E88E5'; // Chat
        case 2:
          return '#8061c2'; // Asistente
        default:
          return 'grey';
      }
    },

    getStatusColor(statusId) {
      switch (statusId) {
        case 1:
          return 'success'; // Activo - Verde
        case 2:
          return 'warning'; // Inactivo - Amarillo
        case 3:
          return 'error';   // Eliminado - Rojo
        default:
          return 'grey';
      }
    },

    openPromptModal(item) {
        this.currentAgentId = item.id;
        try {
            if (item.agent_data) {
                const data = JSON.parse(item.agent_data);
                this.currentPrompt = data.prompt || '';
            } else {
                this.currentPrompt = '';
            }
        } catch (e) {
            this.currentPrompt = item.agent_data || '';
        }
        this.showPromptModal = true;
    },

    closePromptModal() {
      this.showPromptModal = false;
      this.currentPrompt = '';
      this.currentAgentId = null;
      this.promptError = '';
    },

    async savePrompt() {
      try {
        this.savingPrompt = true;
        this.promptError = '';

        await axios.post('/admin/agent-prompt-update', {
          agent_id: this.currentAgentId,
          prompt: this.currentPrompt.trim() // Enviamos el texto directamente
        });

        // Actualizar la lista
        await this.fetchAgents();
        this.closePromptModal();

        this.$vs.notify({
          title: 'Prompt actualizado',
          text: 'Prompt actualizado correctamente',
          color: 'success',
          icon: 'check_box',
          position: 'bottom-center',
          time: 5000,
        });

      } catch (error) {
        this.promptError = error.response?.data?.message || 'Error al guardar el prompt';
      } finally {
        this.savingPrompt = false;
      }
    },

    onSearch() {
      this.page = 1;
      this.fetchAgents();
    },

    onOrderChange() {
      this.page = 1;
      this.fetchAgents();
    },

    onPageChange() {
      this.fetchAgents();
    },

    changePerPage(newPerPage) {
      this.perPage = newPerPage;
      this.page = 1;
      this.fetchAgents();
    }
  },

  created() {
    this.fetchAgents();
  }
};
</script>

<style scoped>
.title {
  font-size: 22px !important;
  color: #8061c2;
}

.desc {
  color: #737373;
  font-size: 15px;
}

.v-dialog {
  border-radius: 8px;
}

.headline {
  font-size: 1.25rem !important;
  background-color: #8061c2 !important;
}
</style>
